import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStaff } from "../../api/staffApi";
import { saveClassRoutineDetails, listClassRoutineDetails, updateClassRoutineDetails } from "../../api/classRoutineDetailsApi";
import "../Attendance/Attendance.css";
import "./ClassRoutine.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const subjectApi = createLookupApi("subjects");
const periodApi = createLookupApi("periods");

const URL_TO_TAB = {
  "create-class-routine": "create",
  "class-routine-view": "view",
  "update-class-routine": "update",
};

const TABS = [
  { key: "create", label: "Routine Generate" },
  { key: "view", label: "Routine Info" },
  { key: "update", label: "Routine Info Update" },
];

const BASE_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"];

function to24h(time12) {
  // "9:30 AM" -> "09:30"
  if (!time12) return "";
  const m = time12.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return "";
  let [, h, min, period] = m;
  h = parseInt(h, 10);
  if (period.toUpperCase() === "PM" && h !== 12) h += 12;
  if (period.toUpperCase() === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${min}`;
}

function to12h(time24) {
  // "09:30" -> "9:30 AM"
  if (!time24) return "";
  const [h, min] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${String(min).padStart(2, "0")} ${period}`;
}

/**
 * Direct port of digicampusbd's `create-class-routine` / `class-routine-view`
 * / `update-class-routine` — three tabs of the SAME underlying weekly
 * timetable data (`ClassRoutineDetails`), matching the tab UI the old app
 * itself used for exam-routine and consistent with this migration's own
 * consolidation-via-tabs pattern for closely related old-app pages.
 *
 * One documented simplification: subject/teacher pickers use this app's
 * plain `subjects`/`staff` lookups rather than the old app's class+group-
 * filtered `SubjectConfiguration` list (see MIGRATION.md) — a slightly
 * less-curated dropdown, not a gap in the saved routine data.
 */
export default function ClassRoutineBuilderPage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "create");

  const [classConfigs, setClassConfigs] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [periods, setPeriods] = useState([]);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setTab(URL_TO_TAB[pageUrl] || "create");
  }, [pageUrl]);

  useEffect(() => {
    listClassConfigurations().then(setClassConfigs);
    academicYearApi.list().then(setAcademicYears);
    groupApi.list().then(setGroups);
    subjectApi.list().then(setSubjects);
    listStaff().then(setStaff);
    periodApi.list().then((list) => setPeriods(list.slice().sort((a, b) => a.serial - b.serial)));
  }, []);

  // ---------------- Create tab ----------------
  const [createClassConfigId, setCreateClassConfigId] = useState("");
  const [createAcademicYear, setCreateAcademicYear] = useState("");
  const [createGroupId, setCreateGroupId] = useState("");
  const [includeSaturday, setIncludeSaturday] = useState(false);
  const [roomNo, setRoomNo] = useState("");
  const [grid, setGrid] = useState(null); // [{periodId, periodName, startTime, endTime, days: [{day, subjectId, teacherId}]}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (classConfigs.length > 0 && !createClassConfigId) setCreateClassConfigId(String(classConfigs[0].id));
    if (academicYears.length > 0 && !createAcademicYear) setCreateAcademicYear(academicYears[0].name);
  }, [classConfigs, academicYears]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDays = includeSaturday ? ["Saturday", ...BASE_DAYS] : BASE_DAYS;

  const handleGenerate = () => {
    if (!createGroupId) {
      setError("Please Select Class/Section & Group");
      return;
    }
    setError("");
    setGrid(
      periods.map((p) => ({
        periodId: p.id,
        periodName: p.name,
        startTime: "",
        endTime: "",
        days: activeDays.map((day) => ({ day, subjectId: "", teacherId: "" })),
      }))
    );
  };

  const updateGridCell = (periodId, day, field, value) => {
    setGrid((prev) =>
      prev.map((row) =>
        row.periodId !== periodId
          ? row
          : { ...row, days: row.days.map((d) => (d.day !== day ? d : { ...d, [field]: value })) }
      )
    );
  };

  const updateGridTime = (periodId, field, value) => {
    setGrid((prev) => prev.map((row) => (row.periodId !== periodId ? row : { ...row, [field]: value })));
  };

  const handleCreate = async () => {
    setError("");
    setSuccess("");
    if (!createAcademicYear) return setError("Please Select Academic Year");
    if (!createClassConfigId) return setError("Please Select Class/Section");
    if (!createGroupId) return setError("Please Select Group");

    const period = (grid || [])
      .filter((row) => row.startTime && row.endTime)
      .map((row) => ({
        periodId: row.periodId,
        classTime: `${to12h(row.startTime)}-${to12h(row.endTime)}`,
        dayOfWeek: row.days
          .filter((d) => d.subjectId && d.teacherId)
          .map((d) => ({ dayOfWeek: d.day, subjectId: Number(d.subjectId), teacherId: Number(d.teacherId) })),
      }))
      .filter((row) => row.dayOfWeek.length > 0);

    setSaving(true);
    try {
      await saveClassRoutineDetails({
        academicYear: createAcademicYear,
        classConfigId: createClassConfigId,
        groupId: createGroupId,
        roomNo,
        period,
      });
      setSuccess("Class Routine Save Successfully.");
      setGrid(null);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Error saving class routine.");
    } finally {
      setSaving(false);
    }
  };

  // ---------------- View tab ----------------
  const [viewClassConfigId, setViewClassConfigId] = useState("");
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    if (classConfigs.length > 0 && !viewClassConfigId) setViewClassConfigId(String(classConfigs[0].id));
  }, [classConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewSearch = async () => {
    setError("");
    setViewData(null);
    if (!viewClassConfigId) return setError("Please select a section!");
    setViewLoading(true);
    try {
      const data = await listClassRoutineDetails(viewClassConfigId);
      if (!data.length || !data[0].period || !data[0].period.length) {
        setError("Please create routine!");
      } else {
        setViewData(data[0]);
      }
    } catch (err) {
      setError("Failed to load class routine!");
    } finally {
      setViewLoading(false);
    }
  };

  const viewDynamicDays = useMemo(() => {
    if (!viewData) return [];
    const seen = new Set();
    viewData.period.forEach((p) => p.dayOfWeek.forEach((d) => seen.add(d.dayOfWeek)));
    const order = ["Saturday", ...BASE_DAYS, "Friday"];
    return order.filter((d) => seen.has(d));
  }, [viewData]);

  // ---------------- Update tab ----------------
  const [updClassConfigId, setUpdClassConfigId] = useState("");
  const [updGroupId, setUpdGroupId] = useState("");
  const [updData, setUpdData] = useState(null); // same shape as viewData
  const [updSaturday, setUpdSaturday] = useState(false);
  const [updLoading, setUpdLoading] = useState(false);
  const [updSaving, setUpdSaving] = useState(false);

  useEffect(() => {
    if (classConfigs.length > 0 && !updClassConfigId) setUpdClassConfigId(String(classConfigs[0].id));
    if (groups.length > 0 && !updGroupId) setUpdGroupId(String(groups[0].id));
  }, [classConfigs, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateSearch = async () => {
    setError("");
    setUpdData(null);
    if (!updClassConfigId) return setError("Please select a section!");
    if (!updGroupId) return setError("Please select a group!");
    setUpdLoading(true);
    try {
      const data = await listClassRoutineDetails(updClassConfigId, updGroupId);
      if (!data.length || !data[0].period || !data[0].period.length) {
        setError("Please create routine!");
      } else {
        setUpdData(data[0]);
        setUpdSaturday(data[0].period.some((p) => p.dayOfWeek.some((d) => d.dayOfWeek === "Saturday")));
      }
    } catch (err) {
      setError("Failed to load routine!");
    } finally {
      setUpdLoading(false);
    }
  };

  const updDays = updSaturday ? ["Saturday", ...BASE_DAYS] : BASE_DAYS;

  const updateUpdCell = (periodIndex, day, field, value) => {
    setUpdData((prev) => {
      const period = prev.period.slice();
      const p = { ...period[periodIndex] };
      p.dayOfWeek = p.dayOfWeek.map((d) => (d.dayOfWeek !== day ? d : { ...d, [field]: value }));
      period[periodIndex] = p;
      return { ...prev, period };
    });
  };

  const updateUpdTime = (periodIndex, field, value) => {
    setUpdData((prev) => {
      const period = prev.period.slice();
      period[periodIndex] = { ...period[periodIndex], [`_${field}`]: value };
      return { ...prev, period };
    });
  };

  const handleUpdate = async () => {
    setError("");
    setSuccess("");
    const payload = [];
    updData.period.forEach((p) => {
      // Fall back to the same classTime-derived default shown in the input's
      // `value` prop when the user never touched it — otherwise every period
      // the user didn't explicitly retype its time into gets silently
      // dropped from the update, even though its subject/teacher changed.
      const startTime = p._startTime || to24h((p.classTime || "").split("-")[0]);
      const endTime = p._endTime || to24h((p.classTime || "").split("-")[1]);
      p.dayOfWeek.forEach((d) => {
        if (!updDays.includes(d.dayOfWeek)) return;
        if (!d.id || !d.subjectId || !d.teacherId || !startTime || !endTime) return;
        payload.push({
          id: d.id,
          subjectId: Number(d.subjectId),
          teacherId: Number(d.teacherId),
          roomNo: "",
          classTime: `${to12h(startTime)} - ${to12h(endTime)}`,
        });
      });
    });
    if (payload.length === 0) {
      setError("No routine data found to update!");
      return;
    }
    setUpdSaving(true);
    try {
      await updateClassRoutineDetails(payload);
      setSuccess("Class routine updated successfully!");
    } catch (err) {
      setError("Error updating class routine!");
    } finally {
      setUpdSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header no-print">
        <h1>Class Routine</h1>
      </div>

      <div className="no-print">
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
      </div>

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      {tab === "create" && (
        <div>
          <div className="attendance-filters">
            <label>
              Select Year
              <select value={createAcademicYear} onChange={(e) => setCreateAcademicYear(e.target.value)}>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.name}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Section
              <select value={createClassConfigId} onChange={(e) => setCreateClassConfigId(e.target.value)}>
                {classConfigs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.classConfigName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Group
              <select value={createGroupId} onChange={(e) => setCreateGroupId(e.target.value)}>
                <option value="">Select Group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="checkbox-inline">
              <input type="checkbox" checked={includeSaturday} onChange={(e) => setIncludeSaturday(e.target.checked)} />
              Include Saturday
            </label>
            <label>
              Room No.
              <input type="text" value={roomNo} onChange={(e) => setRoomNo(e.target.value)} placeholder="Room No" />
            </label>
            <button type="button" className="btn-primary" onClick={handleGenerate}>
              Generate
            </button>
          </div>

          {grid && (
            <>
              <div className="table-scroll">
                <table className="attendance-table routine-grid-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      {activeDays.map((d) => (
                        <th key={d}>{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.map((row) => (
                      <tr key={row.periodId}>
                        <td className="routine-period-cell">
                          <strong>{row.periodName}</strong>
                          <div>
                            Start: <input type="time" value={row.startTime} onChange={(e) => updateGridTime(row.periodId, "startTime", e.target.value)} />
                          </div>
                          <div>
                            End: <input type="time" value={row.endTime} onChange={(e) => updateGridTime(row.periodId, "endTime", e.target.value)} />
                          </div>
                        </td>
                        {row.days.map((d) => (
                          <td key={d.day}>
                            <select value={d.subjectId} onChange={(e) => updateGridCell(row.periodId, d.day, "subjectId", e.target.value)}>
                              <option value="">Select Subject</option>
                              {subjects.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                            <select value={d.teacherId} onChange={(e) => updateGridCell(row.periodId, d.day, "teacherId", e.target.value)}>
                              <option value="">Select Teacher</option>
                              {staff.map((t) => (
                                <option key={t.staffId} value={t.staffId}>
                                  {t.nickName || t.staffName}
                                </option>
                              ))}
                            </select>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={handleCreate} disabled={saving}>
                  {saving ? "Saving..." : "Create Routine"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "view" && (
        <div>
          <div className="attendance-filters no-print">
            <label>
              Select Section
              <select value={viewClassConfigId} onChange={(e) => setViewClassConfigId(e.target.value)}>
                {classConfigs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.classConfigName}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn-primary" onClick={handleViewSearch}>
              Search
            </button>
            {viewData && (
              <button type="button" className="btn-secondary" onClick={() => window.print()}>
                Print
              </button>
            )}
          </div>

          {viewLoading ? (
            <p>Loading...</p>
          ) : (
            viewData && (
              <div className="table-scroll">
                <table className="attendance-table routine-view-table">
                  <thead>
                    <tr>
                      <th>Day</th>
                      {viewData.period.map((p) => (
                        <th key={p.periodId}>
                          {p.periodName}
                          <div className="routine-class-time">{p.classTime}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {viewDynamicDays.map((day) => (
                      <tr key={day}>
                        <th>{day}</th>
                        {viewData.period.map((p) => {
                          const d = p.dayOfWeek.find((x) => x.dayOfWeek === day);
                          return (
                            <td key={p.periodId}>
                              {d ? (
                                <>
                                  <div>
                                    <strong>{d.subjectName}</strong>
                                  </div>
                                  <div>{d.teacherName}</div>
                                </>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {tab === "update" && (
        <div>
          <div className="attendance-filters">
            <label>
              Select Section
              <select value={updClassConfigId} onChange={(e) => setUpdClassConfigId(e.target.value)}>
                {classConfigs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.classConfigName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Group
              <select value={updGroupId} onChange={(e) => setUpdGroupId(e.target.value)}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="checkbox-inline">
              <input type="checkbox" checked={updSaturday} onChange={(e) => setUpdSaturday(e.target.checked)} />
              Include Saturday
            </label>
            <button type="button" className="btn-primary" onClick={handleUpdateSearch}>
              Search
            </button>
            {updData && (
              <button type="button" className="btn-primary" onClick={handleUpdate} disabled={updSaving}>
                {updSaving ? "Updating..." : "Update"}
              </button>
            )}
          </div>

          {updLoading ? (
            <p>Loading...</p>
          ) : (
            updData && (
              <div className="table-scroll">
                <table className="attendance-table routine-grid-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      {updDays.map((d) => (
                        <th key={d}>{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {updDays.map((day) => (
                      <tr key={day}>
                        <th>{day}</th>
                        {updData.period.map((p, periodIndex) => {
                          const d = p.dayOfWeek.find((x) => x.dayOfWeek === day);
                          if (!d) return <td key={p.periodId} />;
                          return (
                            <td key={p.periodId}>
                              <select value={d.subjectId || ""} onChange={(e) => updateUpdCell(periodIndex, day, "subjectId", e.target.value)}>
                                <option value="">Select Subject</option>
                                {subjects.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name}
                                  </option>
                                ))}
                              </select>
                              <select value={d.teacherId || ""} onChange={(e) => updateUpdCell(periodIndex, day, "teacherId", e.target.value)}>
                                <option value="">Select Teacher</option>
                                {staff.map((t) => (
                                  <option key={t.staffId} value={t.staffId}>
                                    {t.nickName || t.staffName}
                                  </option>
                                ))}
                              </select>
                              <div>
                                Start: <input type="time" value={p._startTime || to24h((p.classTime || "").split("-")[0])} onChange={(e) => updateUpdTime(periodIndex, "startTime", e.target.value)} />
                              </div>
                              <div>
                                End: <input type="time" value={p._endTime || to24h((p.classTime || "").split("-")[1])} onChange={(e) => updateUpdTime(periodIndex, "endTime", e.target.value)} />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </MainLayout>
  );
}
